import React from 'react';
import { motion } from 'framer-motion';
import { Mic, Star, TrendingUp, Video, ArrowRight } from 'lucide-react';

const PersonalBranding = ({ onContactClick }) => {
  const brandingServices = [
    {
      icon: Star,
      title: 'Brand Identity',
      description: 'Craft a compelling personal narrative and visual identity that sets you apart from the crowd.',
    },
    {
      icon: Mic,
      title: 'Thought Leadership',
      description: 'Position yourself as an industry authority through speaking, publishing, and media presence.',
    },
    {
      icon: Video,
      title: 'Content Strategy',
      description: 'Build a consistent content presence across platforms that amplifies your voice and values.',
    },
    {
      icon: TrendingUp,
      title: 'Online Reputation',
      description: 'Strategically manage and grow your digital footprint for lasting influence and credibility.',
    },
  ];

  return (
    <section className="relative py-24 overflow-hidden" data-testid="personal-branding-section">
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/12267669/pexels-photo-12267669.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Personal Branding"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/90 to-gray-900/80" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-sm uppercase tracking-wider font-bold text-pink-400 bg-pink-500/10 px-4 py-2 rounded-full mb-6 border border-pink-500/20">
              Signature Service
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4">
              Personal Branding <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Excellence</span>
            </h2>
            <p className="text-xl text-white font-semibold mb-4">
              We Shape Identities. We Build Legacies.
            </p>
            <p className="text-gray-300 leading-relaxed mb-6">
              We craft and amplify personal brands that inspire trust, command attention, 
              and create lasting impact across industries, audiences, and digital platforms.
            </p>
            <motion.button
              onClick={onContactClick}
              className="inline-flex items-center gap-2 primary-btn px-6 py-3 rounded-full text-white font-semibold text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="branding-cta-btn"
            >
              Build Your Brand
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {brandingServices.map((service, index) => (
              <motion.div
                key={service.title}
                className="glass-dark p-6 hover:bg-white/10 transition-colors"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`branding-service-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalBranding;